import { Skeleton } from "@mui/material";
import { styled } from "@mui/system";
import { startCase } from "lodash";
import React from "react";
import { trackEvent } from "src/Amplitude";
import api from "src/store/features/api";
import { dateFormatter } from "src/utils/helpers/DateAndTimeHelpers";
import { INewsData } from "./defaults";

export const NewsHeader = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  alignSelf: "stretch",
  gap: "8px",
  fontSize: "14px",
});

export const NewsHeadline = styled("div")(({ theme }) => ({
  display: "flex",
  height: "175px",
  flexDirection: "column",
  alignItems: "flex-start",
  alignSelf: "stretch",

  [theme.breakpoints.down(720)]: {
    height: "auto",
  },
}));

export const NewsRow = styled("a")(({ theme }) => ({
  display: "flex",
  padding: "8px 0px",
  justifyContent: "space-between",
  alignItems: "flex-start",
  alignSelf: "stretch",
  cursor: "pointer",
  color: "inherit" /* Use the parent's color */,
  textDecoration: "none" /* Remove underline */,

  "&:hover": {
    opacity: 0.7,
  },

  [theme.breakpoints.down(720)]: {
    flexDirection: "column",
    gap: "8px",
    borderBottom: `1px solid ${theme.palette.secondary.contrastText}`,
    marginBottom: "8px",
  },
}));

export const NewsTitle = styled("div")(({ theme }) => ({
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  color: theme.palette.text.primary,
  fontSize: "14px",
  textAlign: "justify",
  fontFamily: "Roobert",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "19px",

  [theme.breakpoints.down(720)]: {
    fontSize: "16px",
    fontWeight: 500,
    whiteSpace: "wrap",
  },
}));

export const NewsSourceDate = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "flex-start",
  gap: "8px",
  whiteSpace: "nowrap",
  paddingLeft: "8px",
  color: theme.palette.mode === "light" ? "#003C63" : "#FFEDE1",
  fontSize: "14px",
  textAlign: "justify",
  fontFamily: "Söhne",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "17px",

  [theme.breakpoints.down(720)]: {
    justifyContent: "space-between",
    alignSelf: "stretch",
    gap: "0px",
    paddingLeft: "0px",
  },
}));

const News = () => {
  // call api here
  const { data: news, isLoading } = api.useGetDashboardCryptoNewsQuery();

  // format data from BE for News component
  const data = React.useMemo((): INewsData[] => {
    if (!news) return [];

    return news.map(
      (news): INewsData => ({
        title: news.title,
        source: startCase(news.source),
        date: dateFormatter(news.timestamp, false, true),
        link: news.link,
      }),
    );
  }, [news]);

  const handleAmplitudeTracking = (title: string) => {
    trackEvent("News Link", { title }, "News");
  };

  if (isLoading) return <Skeleton variant="rounded" width={"100%"} height={"246px"} />;

  return (
    <>
      <NewsHeader>News</NewsHeader>
      <NewsHeadline>
        {data.map((news) => (
          <NewsRow
            key={news.title}
            href={news.link}
            target="_blank"
            onClick={() => handleAmplitudeTracking(news.title)}>
            <NewsTitle>{news.title}</NewsTitle>
            <NewsSourceDate>
              <div>{news.source}</div>
              <div translate="no">{news.date}</div>
            </NewsSourceDate>
          </NewsRow>
        ))}
      </NewsHeadline>
    </>
  );
};

export default News;
